/**
 * DiagnoseCard — Root-Cause Assistance for a single device.
 *
 * The device-page companion to the palette's AskPanel. Where Ask answers a free-text question from
 * the knowledge base, this answers one concrete device question — "why is this device unhappy, and
 * what do I do next?" — by pulling the device's LIVE facts from owgw, classifying the likely failure
 * domain, and grounding a short explanation in those facts + the KB.
 *
 * READ-ONLY / ADVISORY: it retrieves, classifies and recommends; it changes NOTHING. The button
 * needs no RoleGate and no confirm step — the server's read_only floor lets any operator run it. It
 * IS fired explicitly (the button), never on render, because it reaches owgw.
 *
 * MODEL TEXT IS MSP-SIDE ONLY (AI governance P0-A, 2026-10-20). The verdict, findings and next steps
 * are deterministic and shown to everyone. The model's `narrative` is returned by the server ONLY to
 * msp_admin / platform_admin; the RoleGate around it here merely mirrors that rule (it is not the
 * control), and the narrative always carries an "AI-generated" label with the model that wrote it.
 *
 * Reuses the AI surface patterns: the citation list is identical to AskPanel's, and the verdict uses
 * the canonical StatusPill ramp (the ONE sanctioned place status colors become a label). Light/dark
 * come for free from the design tokens (bg-surface-2/border-line/text-ink/ink-muted/ink-subtle).
 */
import { useDiagnoseDevice } from "@/lib/api/hooks";
import { RoleGate } from "@/components/auth/RoleGate";
import { Button } from "@/components/ui/Button";
import { StatusPill } from "@/components/ui/StatusPill";
import type { DiagnosisDomain, DiagnosisLikelihood, HealthState } from "@/types/domain";

/** Operator-facing label for each failure domain (the headline verdict). */
const DOMAIN_LABEL: Record<DiagnosisDomain, string> = {
  telemetry_unavailable: "No live data",
  certificate: "Certificate",
  connectivity: "Connectivity",
  uplink: "Uplink",
  healthy: "Healthy",
  unknown: "Inconclusive",
};

/** Map a (domain, likelihood) onto the StatusPill ramp. A healthy verdict is healthy; a confident
 *  fault is critical; a lower-confidence fault is a warning; "no data"/unknown stay neutral info. */
function pillTone(domain: DiagnosisDomain, likelihood: DiagnosisLikelihood): HealthState | "info" {
  if (domain === "healthy") return "healthy";
  if (domain === "telemetry_unavailable" || domain === "unknown") return "info";
  return likelihood === "high" ? "critical" : "warn";
}

const LIKELIHOOD_LABEL: Record<DiagnosisLikelihood, string> = {
  high: "High confidence",
  medium: "Medium confidence",
  low: "Low confidence",
};

export function DiagnoseCard({ serial }: { serial: string }) {
  const diagnose = useDiagnoseDevice();
  const result = diagnose.data;

  return (
    <section>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">
          Root-cause
        </h3>
        <Button
          variant="secondary"
          disabled={diagnose.isPending}
          onClick={() => diagnose.mutate(serial)}
        >
          {diagnose.isPending ? "Diagnosing…" : result ? "Re-run" : "Diagnose"}
        </Button>
      </div>

      {!result && !diagnose.isPending && !diagnose.isError && (
        <p className="text-xs text-ink-subtle">
          Reads the device's live state from the gateway and suggests the most likely cause and next
          step. Read-only — it changes nothing.
        </p>
      )}

      {diagnose.isError && (
        <p className="text-sm text-critical">Couldn't run the diagnosis. Try again.</p>
      )}

      {result && !diagnose.isPending && (
        <div className="flex flex-col gap-3 rounded-md border border-line bg-surface-2 p-3">
          {/* Verdict: failure domain + our confidence in it. */}
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill status={pillTone(result.failure_domain, result.likelihood)}>
              {DOMAIN_LABEL[result.failure_domain]}
            </StatusPill>
            <span className="text-xs text-ink-muted">{LIKELIHOOD_LABEL[result.likelihood]}</span>
            {!result.grounded && (
              <span className="inline-flex items-center rounded-full bg-surface px-2 py-0.5 text-xs text-ink-muted">
                Not from the knowledge base
              </span>
            )}
          </div>

          {/* AI narrative — msp_admin+ only (the server never sends it to customer roles; this
              gate mirrors that). Labelled as model output so it cannot pass for a finding. */}
          {result.narrative && (
            <RoleGate min="msp_admin">
              <div
                className="flex flex-col gap-1 rounded border border-line bg-surface px-3 py-2"
                data-testid="diagnose-narrative"
              >
                <p className="text-xs font-medium uppercase tracking-wide text-ink-subtle">
                  AI-generated{result.model ? ` · ${result.model}` : ""}
                </p>
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink">
                  {result.narrative}
                </p>
                <p className="text-xs text-ink-subtle">
                  Model output — check it against the findings before acting.
                </p>
              </div>
            </RoleGate>
          )}

          {/* Findings — the deterministic evidence the verdict rests on (never model text). */}
          {result.findings.length > 0 && (
            <div className="flex flex-col gap-1">
              <p className="text-xs font-medium uppercase tracking-wide text-ink-subtle">Findings</p>
              <ul className="flex flex-col gap-1">
                {result.findings.map((f, i) => (
                  <li key={i} className="text-sm leading-relaxed text-ink">
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Ranked next actions — advisory steps, in priority order. */}
          {result.next_actions.length > 0 && (
            <div className="flex flex-col gap-1 border-t border-line pt-2">
              <p className="text-xs font-medium uppercase tracking-wide text-ink-subtle">
                Suggested next steps
              </p>
              <ol className="flex list-decimal flex-col gap-1 pl-5">
                {result.next_actions.map((a, i) => (
                  <li key={i} className="text-sm leading-relaxed text-ink">
                    {a}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Sources — identical shape to AskPanel so the two AI surfaces cite alike. */}
          {result.citations.length > 0 && (
            <div className="flex flex-col gap-1 border-t border-line pt-2">
              <p className="text-xs font-medium uppercase tracking-wide text-ink-subtle">Sources</p>
              {result.citations.map((c) => (
                <div key={c.n} className="text-xs text-ink-muted">
                  <span className="text-ink-subtle">[{c.n}]</span>{" "}
                  {c.url ? (
                    <a
                      href={c.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-brand hover:underline"
                    >
                      {c.title}
                    </a>
                  ) : (
                    <span className="text-ink">{c.title}</span>
                  )}
                  {c.heading && <span className="text-ink-subtle"> · {c.heading}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
